/**
 * Enhanced Product Comparison Section
 * Handles tab switching and competitor selection for dynamic comparison sections
 */
class ProductComparison {
  constructor(container) {
    this.container = container;
    this.select = container.querySelector('[data-competitor-select]');
    this.tabs = container.querySelectorAll('[data-comparison-tab]');
    this.competitors = container.querySelectorAll('[data-competitor]');
    this.competitorOnes = container.querySelectorAll('[data-competitor-one]');
    this.competitorTwos = container.querySelectorAll('[data-competitor-two]');
    this.competitorThrees = container.querySelectorAll('[data-competitor-three]');
    this.competitorFours = container.querySelectorAll('[data-competitor-four]');
    
    // Bind methods to maintain context
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);
    
    this.init();
  }

  init() {
    if (!this.validateElements()) {
      console.warn('Product Comparison: Missing required elements');
      return;
    }

    this.bindEvents();
    this.setInitialState();
  }

  validateElements() {
    return !!(this.select && this.tabs.length > 0 && this.competitors.length > 0);
  }

  bindEvents() {
    // Select dropdown change event
    this.select.addEventListener('change', this.handleSelectChange);

    // Tab click events
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick);
      tab.addEventListener('keydown', this.handleTabKeydown);
    });
  }

  handleSelectChange(e) {
    this.switchToCompetitor(e.target.value);
  }

  handleTabClick(e) {
    const tab = e.currentTarget;
    const tabIndex = tab.getAttribute('data-comparison-tab');
    if (tabIndex) {
      this.switchToCompetitor(tabIndex);
      this.setActiveTab(tab);
    }
  }

  handleTabKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleTabClick(e);
    }
  }

  setInitialState() {
    // Set initial active state based on first tab
    const firstTab = this.tabs[0];
    if (firstTab) {
      this.setActiveTab(firstTab);
      const initialIndex = firstTab.getAttribute('data-comparison-tab');
      if (initialIndex) {
        this.switchToCompetitor(initialIndex);
      }
    }
  }

  switchToCompetitor(index) {
    // Remove active class from all competitor elements
    this.hideAllCompetitors();
    
    // Show the selected competitor
    this.showCompetitor(index);
    
    // Update select dropdown to match
    this.updateSelectValue(index);
  }

  hideAllCompetitors() {
    this.competitors.forEach((competitor) => {
      competitor.classList.remove('is-active');
    });
    
    this.competitorOnes.forEach((competitor) => {
      competitor.classList.remove('is-active');
    });
    
    this.competitorTwos.forEach((competitor) => {
      competitor.classList.remove('is-active');
    });
    
    this.competitorThrees.forEach((competitor) => {
      competitor.classList.remove('is-active');
    });
    
    this.competitorFours.forEach((competitor) => {
      competitor.classList.remove('is-active');
    });
  }

  showCompetitor(index) {
    // Show based on index
    switch (index) {
      case '1':
        this.competitorOnes.forEach((competitor) => {
          competitor.classList.add('is-active');
        });
        break;
      case '2':
        this.competitorTwos.forEach((competitor) => {
          competitor.classList.add('is-active');
        });
        break;
      case '3':
        this.competitorThrees.forEach((competitor) => {
          competitor.classList.add('is-active');
        });
        break;
      case '4':
        this.competitorFours.forEach((competitor) => {
          competitor.classList.add('is-active');
        });
        break;
      default:
        // For dynamic tabs, show by data-competitor attribute
        this.competitors.forEach((competitor) => {
          const competitorIndex = competitor.getAttribute('data-competitor');
          if (competitorIndex === index) {
            competitor.classList.add('is-active');
          }
        });
    }
  }

  setActiveTab(activeTab) {
    // Remove active class from all tabs
    this.tabs.forEach((tab) => {
      tab.classList.remove('is-active');
    });
    
    // Add active class to clicked tab
    activeTab.classList.add('is-active');
  }

  updateSelectValue(index) {
    if (this.select && this.select.value !== index) {
      this.select.value = index;
    }
  }

  // Public method to switch to a specific competitor
  switchTo(index) {
    this.switchToCompetitor(index);
    
    // Update active tab
    const targetTab = this.container.querySelector(`[data-comparison-tab="${index}"]`);
    if (targetTab) {
      this.setActiveTab(targetTab);
    }
  }

  // Public method to get current active competitor
  getCurrentCompetitor() {
    return this.select ? this.select.value : null;
  }

  // Destroy method to clean up event listeners
  destroy() {
    this.select.removeEventListener('change', this.handleSelectChange);
    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this.handleTabClick);
      tab.removeEventListener('keydown', this.handleTabKeydown);
    });
  }
}

// Initialize all product comparison sections on the page
function initProductComparisons() {
  const containers = document.querySelectorAll('[data-product-comparison-container]');
  
  containers.forEach((container) => {
    // Skip if already initialized
    if (container.classList.contains('is-initialised')) {
      return;
    }
    
    // Mark as initialized
    container.classList.add('is-initialised');
    
    // Create new instance
    const comparison = new ProductComparison(container);
    
    // Store instance on container for potential external access
    if (typeof container !== 'undefined') {
      container.productComparison = comparison;
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductComparisons);
} else {
  initProductComparisons();
}

// Re-initialize if new sections are loaded dynamically (e.g., via AJAX)
window.addEventListener('load', () => {
  setTimeout(initProductComparisons, 100);
});

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { ProductComparison, initProductComparisons };
}

// Global access for debugging
if (typeof window !== 'undefined') {
  window.ProductComparison = ProductComparison;
  window.initProductComparisons = initProductComparisons;
}
